import './../libs/jquery-touch.js';
import { push } from './../store/history.js';
import { eventType } from './../config/settings.js';
import { handleDoubleEvents, emitter } from './../utils/custom-events.js';
import { setNavigationCookies } from './../store/cookies.js';
import { handleLoveEvents } from './mygames.js';
import { getAllGames as hydrate } from './allgames.js';
import { store } from './store.js';
/**
 * This is similar to Higher Order Component (HOC) @see https://facebook.github.io/react/docs/higher-order-components.html
 * It's important that this module doesn't have any side effects as there is no view mapped to it.
 * Though it must setup common needs of children modules/components.
 * If a setup is not common to more than one child or setup can be delayed,
 * it's a sign that this module might not be right place for that setup.
 *
 * ## Children modules
 * - Explore (the default view)
 * - All Games
 * - My games
 * - Live Casino
 *
 * ## Setup list
 * - Preload all games data
 * - Add common EventListeners
 */

/**
 * All Games and My Games both views filter and render games depending on which tab is selected
 * value of tab(which is a radio button) === value of game object key, which is used as filter
 * if value is empty string all game result will be returned
 *
 * ## All Games view - Tab <==> Filter
 * - All === ""
 * - Jackpots === 'jackpot'
 * - Hot === 'isHot'
 * - New === 'isNew'
 *
 * Game filter is changed on each main tab change using 'tabbed' event and for sub tabs using 'change' event on radio buttons.
 */
function gamesFilter() {
	const filter = document.querySelector('.tabs-container section[role="tabpanel"]:not([aria-hidden="true"]) .user-options .filters input[type="radio"]:checked');
	if (filter) {
		return filter.value;
	}
	return null;
}

function setGamesFilter() {
	return store.get().filters.set({ key: gamesFilter() });
}
/**
 * Double click Handlers for game items.
 * @param {object} $game - game DOM node on which double click was observed
 */
function doubleClickOnGameItem($game) {
	const $heart = $game.getElementsByClassName('game-item__love')[0];
	handleLoveEvents({ target: $heart });
	$heart.classList.toggle('shown');

	setTimeout(() => {
		$heart.classList.remove('shown');
		$heart.classList.add('fade');
		setTimeout(() => {
			$heart.classList.remove('fade');
		}, 500);
	}, 1000);
}

/**
 * Gets session from cookie and call loadGame with parameters
 * @param {object} game - game DOM node, on which click event was fired.
 * @param {string} mode - One of 'fun' or 'real'. Default is 'real'.
 * 'real' mode requires user to be authenticated.
 * @todo
 * right now if user is not authenticated and requests to play 'real' mode game,
 * user is presented with login page but it doesn't redirect user back to game play page.
 * This is problem in many other private routes.
 */
function checkSessionAndLoadGame(game, mode = 'real') {
	push(`/casino/game/${encodeURIComponent(game.dataset.gamecode)}/${mode}`);
}

/**
 * Event is captured from bubbeling.
 * In case there is a doble click within timespan of 300  milisecond and target is game title or game image,
 * then it will love the target game item else it will navigate to game play page.
 * @param {object} e - event object
 */
function processDoubleEvents(e) {
	const { target } = e;
	const { classList } = target;
	if (classList.contains('game-item__img') || (classList.contains('main-title') && target.parentElement.classList.contains('game-item__title-wrapper'))) {
		e.preventDefault();
		const $game = target.closest('.game-item');
		handleDoubleEvents(e, $game, checkSessionAndLoadGame, doubleClickOnGameItem);
	}
}

/**
 * Event is captured from bubbeling.
 * Opens support navigation overlay {'.support-nav'} on click of kebab menu (three dots) {'.dots-container'} on game item.
 * Support navigation overlay features three action links.
 * 	- .js-real-mode a - onclick of this link navigate to game play page in real mode.
 * 	- .js-fun-mode a - onclick of this link navigate to game play page in fun mode.
 * 	- .js-close-support-nav - onclick of this link close the support-navigation overlay
 *
 * Using event listener option 'once: true' will remove event listener after first invocation
 * https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Parameters
 * @param {object} e - event object
 */
function processSupportNavEvents(e) {
	const { target } = e;
	const { classList } = target;
	if (classList.contains('dots-container')) {
		e.preventDefault();
		const supportNav = document.getElementsByClassName('support-nav')[0];
		supportNav.classList.toggle('support-nav--is-open');
		const $game = target.closest('.game-item');

		supportNav.querySelector('.js-real-mode a').addEventListener(eventType, () => {
			checkSessionAndLoadGame($game);
		}, {
			capture: true,
			once: true
		});

		supportNav.querySelector('.js-fun-mode a').addEventListener(eventType, () => {
			checkSessionAndLoadGame($game, 'fun');
		}, {
			capture: true,
			once: true
		});

		supportNav.querySelector('.js-close-support-nav').addEventListener(eventType, () => {
			supportNav.classList.toggle('support-nav--is-open');
		}, {
			capture: true,
			once: true
		});
	}
}

/**
 * Initialize event handlers for casino.
 * Handler to love/like a game is defined in mygames.js
 * Using function as event handler will not add duplicate handler as opposed to anonymous functions.
 * It also leverages event bubbeling. So event listener is added to parent with presumebly is .tabs-container.
 * @listens {click} listens for eventType defined in './../config/settings.js', which is click event
 */
function initEventListners() {
	const tabContainer = document.getElementsByClassName('tabs-container');
	if (tabContainer && tabContainer.length > 0) {
		tabContainer[0].addEventListener(eventType, processSupportNavEvents);
		tabContainer[0].addEventListener(eventType, handleLoveEvents);
		tabContainer[0].addEventListener(eventType, processDoubleEvents);
		emitter.on('tabbed', setGamesFilter);
	}

	const filters = document.querySelectorAll('.user-options .filters input[type="radio"]');
	if (filters && filters.length > 0) {
		for (const filter of filters) {
			filter.addEventListener('change', setGamesFilter);
		}
	}
}

/**
 * Initialize Casino module.
 *
 * @todo create a destroy function
 * @example
 * import { init } from ./path/to/casino.js;
 * init();
 */
function init() {
	hydrate();
	setNavigationCookies('casino');
	initEventListners();
}

export { init, gamesFilter, checkSessionAndLoadGame, processDoubleEvents, doubleClickOnGameItem, processSupportNavEvents };



// WEBPACK FOOTER //
// ./mobile-core/src/casino/casino.js